import { filter, map, some, find, get, has, keys } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';

import { AnswerType } from './useAnswersFilter';
import { RootState } from 'src/actions/configuration/rootReducer';
import searchDropdownResource from 'src/actions/searchDropdown/searchDropdownResource';
import useCurrentAnswer from 'src/presentation/components/pages/shared/hooks/useCurrentAnswer';

const getAnswerName = (answer, language: string) =>
  find(answer.contents, content => has(content, 'selfService') && content.language === language)?.selfService?.name;

const isAnswerPublic = (answer, language) =>
  some(
    get(answer, 'contents', []),
    content => content && has(content, 'selfService.bodyHtml') && content.language === language
  );

const getAnswersOptions = ({ answers, getName = answer => answer.name }) => {
  return map(answers, answer => ({ label: getName(answer), value: answer.id }));
};

const getAllAnswers = ({ answers, language, answerType }) => {
  if (answerType === AnswerType.PUBLIC) {
    const publicAnswers = filter(answers, answer => isAnswerPublic(answer, language));
    return getAnswersOptions({ answers: publicAnswers, getName: answer => getAnswerName(answer, language) });
  }

  return getAnswersOptions({ answers, getName: answer => answer.name });
};

export default function useSearchDropdownAnswers(
  fieldName,
  language,
  answerType: AnswerType = AnswerType.PUBLIC,
  currentAnswerId?: string
) {
  const searchState = useSelector((state: RootState) => state[searchDropdownResource.name]);
  const dispatch = useDispatch();

  const { value: currentAnswer } = useCurrentAnswer(currentAnswerId);

  return useMemo(() => {
    const answers = searchState[fieldName]?.value ?? [];

    if (currentAnswer && keys(currentAnswer).length) {
      dispatch(searchDropdownResource.actions.addAnswer({ fieldName, answer: currentAnswer }));
    }

    return getAllAnswers({ answers, language, answerType });
  }, [dispatch, searchState, currentAnswer, language, answerType, fieldName]);
}
